import { yupResolver } from '@hookform/resolvers/yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Divider,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { Can } from 'core/components/permissions/Can';
import PhoneFieldElement from 'core/components/PhoneField/PhoneFieldElement';
import { enum2NumberArray, isNumeric } from 'core/utils/commonUtils';
import { getOrderActionName } from 'core/utils/orderUtils';
import { getRoleName } from 'core/utils/userUtils';
import { passwordValidator, phoneValidator } from 'core/utils/validators/common';
import useDocuments from 'hooks/swr/useDocuments';
import useUser from 'hooks/swr/useUser';
import useUserDocuments from 'hooks/swr/useUserDocuments';
import CardedPage from 'layout/pages/CardedPage';
import { isEmpty, isNil, startCase } from 'lodash';
import { OrderActionEnum } from 'models/enums/orderActionEnum';
import { UserRoleEnum } from 'models/enums/userRoleEnum';
import type { User } from 'models/user/user';
import type { FC } from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  AutocompleteElement,
  CheckboxElement,
  FormContainer,
  PasswordElement,
  SelectElement,
  TextFieldElement,
  useForm,
} from 'react-hook-form-mui';
import { useNavigate, useParams } from 'react-router-dom';
import userService from 'services/userService';
import StoreContext from 'stores/context/StoreContext';
import { array, lazy, number, object, string } from 'yup';
import DocumentSelector from './DocumentSelector';
import RoleComparisonDialog from './RoleComparisonDialog';
import UserEditPageHeader from './UserEditPageHeader';
import UserPageSkeleton from './UserPageSkeleton';

interface UserEditFormData {
  name: string;
  surname: string;
  patronymic: string;
  actionAccesses: OrderActionEnum[];
  phone: string;
  password: string;
  role: UserRoleEnum;
  blocked: boolean;
  documentAccesses: number[];
  mangoExtension: string;
}

const defaultUser: User = {
  id: 0,
  name: '',
  patronymic: '',
  surname: '',
  phone: '7',
  role: UserRoleEnum.Manager,
  actionAccesses: [],
  blocked: false,
  mangoExtension: '',
};

const title = 'Сотрудник';

const UserEditPage: FC = () => {
  const { notifyStore } = useContext(StoreContext);
  const { userId } = useParams();
  const [openRoleComparisonDialog, setOpenRoleComparisonDialog] = useState(false);
  const navigate = useNavigate();
  const isNewUser = userId === 'new';

  const { data: userData, isLoading: isUserLoading } = useUser(
    +(userId ?? 0),
    isNumeric(userId ?? '') && !isNewUser
  );

  const { data: allDocuments, isLoading: isAllDocumentsLoading } = useDocuments();
  const { data: userDocuments, isLoading: isUserDocumentsLoading } = useUserDocuments(
    +(userId ?? 0),
    !isNewUser
  );

  const user = useMemo(() => (isNil(userData) ? defaultUser : userData), [userData]);
  const isLoading = isUserLoading || isAllDocumentsLoading || isUserDocumentsLoading;

  const schema = object<UserEditFormData>({
    surname: string()
      .required()
      .max(300)
      .transform((v) => (isEmpty(v) ? '' : startCase(v))),
    name: string()
      .required()
      .max(300)
      .transform((v) => (isEmpty(v) ? '' : startCase(v))),
    patronymic: string()
      .max(300)
      .transform((v) => (isEmpty(v) ? '' : startCase(v))),
    phone: phoneValidator.required(),
    actionAccesses: array().of(number()),
    password: lazy(() =>
      user?.id !== 0 ? string().notRequired() : passwordValidator.required()
    ),
    role: number().oneOf(enum2NumberArray(UserRoleEnum)).required(),
    documentAccesses: array().of(number()),
    mangoExtension: string().max(50).optional(),
  }).required();

  const formContext = useForm<UserEditFormData>({
    mode: 'onTouched',
    values: {
      phone: user?.phone ?? '',
      surname: user?.surname ?? '',
      name: user?.name ?? '',
      patronymic: user?.patronymic ?? '',
      actionAccesses: user?.actionAccesses ?? [],
      password: '',
      role: user?.role ?? UserRoleEnum.Manager,
      blocked: user?.blocked ?? false,
      documentAccesses: userDocuments?.map((d) => d.id) ?? [],
      mangoExtension: user?.mangoExtension || '',
    },
    resolver: yupResolver(schema),
  });

  const { isSubmitting } = formContext.formState;

  useEffect(() => {
    if (userDocuments && !isNewUser) {
      formContext.setValue(
        'documentAccesses',
        userDocuments.map((doc) => doc.id)
      );
    }
  }, [userDocuments, isNewUser, formContext]);

  const cancelEditing = (): void => {
    navigate(`/users${isNumeric(userId ?? '') ? `/${userId ?? ''}` : ''}`);
  };

  const saveUser = async (d: UserEditFormData): Promise<void> => {
    try {
      const response =
        user?.id === 0
          ? await userService.createUserAsync({
              name: d.name,
              actionAccesses: d.actionAccesses,
              password: d.password,
              patronymic: d.patronymic,
              phone: d.phone,
              surname: d.surname,
              role: d.role,
              mangoExtension: d.mangoExtension,
            })
          : await userService.updateUserAsync(user?.id ?? 0, {
              name: d.name,
              actionAccesses: d.actionAccesses,
              patronymic: d.patronymic,
              phone: d.phone,
              surname: d.surname,
              role: d.role,
              blocked: d.blocked,
              mangoExtension: d.mangoExtension,
            });

      await userService.putDocumentAccessesAsync(response.id, d.documentAccesses);

      notifyStore.showNotify('Изменения сохранены', 'success');
      navigate(`/users/${response.id}`);
    } catch (err) {
      console.error(err);
      notifyStore.showNotify('Не удалось сохранить пользователя', 'error');
    }
  };

  const saveInvalidData = (): void => {
    notifyStore.showNotify(
      'Не удалось сохранить пользователя. Введенные данные не корректны',
      'error'
    );
  };

  const orderActions = useMemo(() => enum2NumberArray(OrderActionEnum), []);

  const handleRoleComparisonDialogClose = (): void => {
    setOpenRoleComparisonDialog(false);
  };
  const handleRoleComparisonDialogOpen = (): void => {
    setOpenRoleComparisonDialog(true);
  };

  if (isLoading || (isNil(userData) && !isNewUser)) {
    return (
      <CardedPage title={title}>
        <UserPageSkeleton />
      </CardedPage>
    );
  }

  return (
    <CardedPage
      title={title}
      header={!isNewUser ? <UserEditPageHeader user={user} /> : undefined}>
      <FormContainer
        FormProps={{ style: { maxWidth: '600px' } }}
        formContext={formContext}
        onSuccess={saveUser}
        onError={saveInvalidData}>
        <TextFieldElement label='Фамилия' margin='normal' fullWidth name='surname' />
        <TextFieldElement label='Имя' margin='normal' fullWidth name='name' />
        <TextFieldElement label='Отчество' margin='normal' fullWidth name='patronymic' />

        <Stack direction='row' alignItems={'center'}>
          <SelectElement
            fullWidth
            margin='normal'
            label='Роль'
            name='role'
            options={enum2NumberArray(UserRoleEnum).map((v) => ({
              id: v,
              label: getRoleName(v),
            }))}
          />

          <IconButton
            aria-label='info'
            sx={{ mt: 1, ml: 0.5 }}
            edge='end'
            size='small'
            onClick={handleRoleComparisonDialogOpen}>
            <InfoOutlinedIcon />
          </IconButton>
        </Stack>

        <PhoneFieldElement label='Телефон' name='phone' margin='normal' fullWidth />
        {isNewUser && (
          <PasswordElement label='Пароль' name='password' margin='normal' fullWidth />
        )}

        <AutocompleteElement
          label='Работает на этапах'
          multiple
          name='actionAccesses'
          matchId
          textFieldProps={{ margin: 'normal' }}
          options={orderActions.map((v) => ({
            id: v,
            label: getOrderActionName(v),
          }))}
          showCheckbox
        />

        <DocumentSelector
          value={formContext.watch('documentAccesses')}
          onChange={(newIds) => {
            formContext.setValue('documentAccesses', newIds);
          }}
          allDocuments={allDocuments}
          isLoading={isAllDocumentsLoading || isUserDocumentsLoading}
        />

        {!isNewUser && (
          <Stack direction={'row'} alignItems={'center'}>
            <CheckboxElement label={'Заблокирован'} name='blocked' />
            <Tooltip
              title='При блокировке пользователь будет немедленно отключен от системы. Он не сможет авторизоваться повторно и станет недоступен для выбора в любых списках пользователей.'
              placement='right'>
              <InfoOutlinedIcon fontSize='small' />
            </Tooltip>
          </Stack>
        )}

        <Can I='update' a='MangoUserSettings'>
          <Typography variant='subtitle1' sx={{ mt: 2, mb: 1 }}>
            Mango Office
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <TextFieldElement
            label='Добавочный номер'
            margin='normal'
            fullWidth
            name='mangoExtension'
          />
        </Can>

        <Toolbar disableGutters>
          <Stack spacing={1} direction='row'>
            <LoadingButton
              loading={isSubmitting}
              loadingPosition={'start'}
              variant='contained'
              type='submit'
              startIcon={<SaveIcon />}>
              <span>{isNewUser ? 'Создать' : 'Сохранить'}</span>
            </LoadingButton>
            <Button
              disabled={isSubmitting}
              variant='outlined'
              onClick={cancelEditing}
              color='secondary'>
              Отмена
            </Button>
          </Stack>
        </Toolbar>
      </FormContainer>

      <RoleComparisonDialog
        open={openRoleComparisonDialog}
        onClose={handleRoleComparisonDialogClose}
      />
    </CardedPage>
  );
};

export default UserEditPage;
